import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { RiskLevel } from '~/types/RiskLevel';
import { useSelectRisk } from '~/controllers/agent';
import { useDirection } from '~/hooks/i18nHooks';
import { Select } from '~/components';
import { DirectionProp } from '~/types/Direction';
import direction from '~/utils/direction';
import colors from '~/constants/colors';

const riskLevels: Record<string, RiskLevel> = {
  normal: 'NORMAL',
  highRisk: 'HIGH_RISK',
};

interface Props {
  currentValue?: RiskLevel;
  type: 'LEAD' | 'APPLICATION';
  recordId?: string;
  disabled?: boolean;
  onAfterChange?: () => void;
}

const RiskLevelSelect = (props: Props) => {
  const { currentValue, type, recordId, onAfterChange, disabled } = props;
  const selectRisk = useSelectRisk();
  const dir = useDirection();
  const [t] = useTranslation();

  const options = Object.values(riskLevels).map((value) => ({
    label: t(value),
    value,
    key: value,
  }));

  const onSelect = (riskLevel: string) => {
    if (type === 'APPLICATION' && recordId) {
      selectRisk
        .mutateAsync({ applicationUid: recordId, level: riskLevel })
        .then(() => {
          onAfterChange?.();
        })
        .catch(() => {});
    }
  };

  if (!currentValue) return null;

  return (
    <Container dir={dir} tag={currentValue} disabled={disabled}>
      <Select
        id="riskLevelCell"
        onClick={(e) => {
          e.preventDefault();
        }}
        value={currentValue}
        disabled={disabled}
        options={options}
        size="small"
        onSelect={onSelect}
        dropdownMatchSelectWidth={false}
      />
    </Container>
  );
};

const Container = styled.div<
  DirectionProp & {
    tag: RiskLevel;
    disabled?: boolean;
  }
>`
  ${({ disabled }) =>
    disabled
      ? css`
          .ant-select-arrow {
            display: none;
          }
          .ant-select-selection-item {
            ${direction.padding.right('1px !important')};
            ${direction.padding.left('1px !important')};
          }
        `
      : css``}
  .ant-select-selection-item {
    font-weight: 500;
  }

  .ant-select:not(.ant-select-customize-input) .ant-select-selector,
  .ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    box-shadow: 0 0 0;
  }

  ${({ tag }) =>
    tag === 'NORMAL'
      ? css`
          .ant-select-selection-item {
            color: ${colors.normalTagText} !important;
          }
          .ant-select-selector {
            border-color: ${colors.normalTagText} !important;
            background-color: ${colors.normalTagBackground} !important;
          }
        `
      : css`
          .ant-select-selection-item {
            color: ${colors.highRiskTagText} !important;
          }
          .ant-select-selector {
            border-color: ${colors.highRiskTagText} !important;
            background-color: ${colors.highRiskTagBackground} !important;
          }
        `}
`;

export default RiskLevelSelect;
